import React from 'react'
import { Button, Form, Input, Modal, Typography } from 'antd'
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { ordersApi } from 'api/orders'
import { transactionsApi } from 'api/transaction'
import { IOrderTransaction, ITransaction } from 'models/transaction'
import { EOrderStatus } from 'models/orders'
import { showServerError } from 'shared/utils/showServerError'
import { getOrderLedger, IOrderLedgerSuperOrder } from 'shared/utils/transactions/getOrderLedger'
import { floatNumbersPattern } from 'shared/utils/patterns'
import { normalizeNumber } from 'shared/utils/normalizeNumber'
import { OrderLedgerTable } from './OrderLedgerTable'
import styles from './TransactionsModal.module.scss'

interface ITransactionsModalProps {
  isOpen: boolean
  orderId: string
  orderStatus: EOrderStatus
  onClose: () => void
}

interface IRefundFormData {
  comment: string
  sum: string
}

export const TransactionsModal: React.FC<ITransactionsModalProps> = React.memo(
  ({ isOpen, orderId, orderStatus, onClose }) => {
    const [transactions, setTransactions] = React.useState<ITransaction[]>([])
    const [orderTransactions, setOrderTransactions] = React.useState<IOrderTransaction[]>([])
    const [superOrder, setSuperOrder] = React.useState<IOrderLedgerSuperOrder | null>(null)
    const [isLoading, setIsLoading] = React.useState(false)

    const [form] = Form.useForm()

    const getTransactions = async () => {
      setIsLoading(true)
      // Запросы независимы: без истории покупателя записи продавца всё равно показываем
      await Promise.all([
        transactionsApi
          .getTransactions({
            skip: '0',
            limit: '500',
            order_id: orderId
          })
          .then((response) => setTransactions(response.data.transactions))
          .catch(showServerError),
        transactionsApi
          .getOrderTransactions(orderId)
          .then((response) => setOrderTransactions(response.data.transactions))
          .catch(showServerError),
        // Супер-заказ нужен только для пояснения к доле платежа — без него лента остаётся верной
        ordersApi
          .getSuperOrder(orderId)
          .then(({ data }) =>
            setSuperOrder({
              number: data.number,
              sum: data.sum,
              payment_id: data.payment_id,
              ordersCount: data.orders.length
            })
          )
          .catch(() => setSuperOrder(null))
      ])
      setIsLoading(false)
    }

    React.useEffect(() => {
      if (isOpen) {
        getTransactions()
      } else {
        form.resetFields()
      }
    }, [isOpen])

    const refundRequest = async (data: IRefundFormData) => {
      try {
        await transactionsApi.createRefundOrderTransaction(orderId, {
          comment: data.comment,
          custom_sum: normalizeNumber(data.sum)
        })
        getTransactions()
        form.resetFields()
      } catch (e) {
        showServerError(e)
      }
    }

    const onSubmitRefundForm = async (data: IRefundFormData) => {
      Modal.confirm({
        centered: true,
        title: 'Внимание!',
        content: 'Возврат средств будет невозможно отменить, после подтверждения.',
        icon: <ExclamationCircleOutlined />,
        okText: 'Подтвердить',
        cancelText: 'Отменить',
        onOk() {
          return new Promise((resolve) => {
            refundRequest(data).then(resolve)
          })
        },
        onCancel() { }
      })
    }

    const ledger = React.useMemo(
      () =>
        getOrderLedger(
          { orderTransactions, sellerTransactions: transactions },
          superOrder
        ),
      [orderTransactions, transactions, superOrder]
    )

    return (
      <Modal
        centered
        open={isOpen}
        onCancel={onClose}
        footer={null}
        wrapClassName={styles.wrapper}
        className={styles.modal}
      >
        <Typography.Title level={3} style={{ marginBottom: 24 }}>
          Транзакции
        </Typography.Title>
        <div className={styles.ledger}>
          <OrderLedgerTable rows={ledger} isLoading={isLoading} />
        </div>
        {orderStatus !== EOrderStatus.ON_CONFIRMATION && orderStatus !== EOrderStatus.CANCELED && orderStatus !== EOrderStatus.NOT_PAID && (
          <>
            <Typography.Title level={3} style={{ marginBottom: 24 }}>
              Возврат
            </Typography.Title>
            <Form
              form={form}
              name='refund'
              className={styles.refundForm}
              onFinish={onSubmitRefundForm}
            >
              <Form.Item
                name='comment'
                label='Причина'
                rules={[
                  {
                    required: true,
                    message: 'Заполните причину возврата'
                  },
                  {
                    whitespace: true,
                    message: 'Заполните причину возврата'
                  }
                ]}
                style={{ flex: 1.3 }}
                hasFeedback
              >
                <Input.TextArea
                  placeholder='Опишите причину возврата'
                  rows={4}
                />
              </Form.Item>
              <div>
                <Form.Item
                  name='sum'
                  label='Сумма'
                  rules={[
                    {
                      required: true,
                      message: 'Введите сумму'
                    },
                    {
                      whitespace: true,
                      message: 'Введите сумму'
                    },
                    {
                      pattern: floatNumbersPattern,
                      message: 'Укажите сумму корректно'
                    }
                  ]}
                  style={{ flex: 1, marginBottom: 17 }}
                  hasFeedback
                >
                  <Input />
                </Form.Item>
                <div className={styles.refundButton}>
                  <Button
                    danger
                    type='primary'
                    htmlType='submit'
                  >
                    Вернуть
                  </Button>
                </div>
              </div>
              <div className={styles.refundTextInfoWrapper}>
                <InfoCircleOutlined className={styles.refundTextInfoIcon} />
                <Typography.Text className={styles.refundTextInfo}>
                  При уже начатом процессе доставки товара (продавец передал
                  товар в службу СДЕК) от статуса “в доставке”, мы не можем
                  вернуть пользователю стоимость доставки.
                </Typography.Text>
              </div>
            </Form>
          </>
        )}
      </Modal>
    )
  }
)
